<!-- ============================================================================= -->
<!-- Ella Custom JS - Customize The Style For Layout -->
<!-- ============================================================================= -->

<!-- ============================================================================= -->
<!-- IMPORTANT DISCLAIMER -->
<!-- Please use only JS to style the layout. -->
<!-- ============================================================================= -->

(() => {
  if (window.bundleRemovalNormalizerLoaded) return;

  window.bundleRemovalNormalizerLoaded = true;

  const root =
    window.Shopify?.routes?.root ||
    window.routes?.root ||
    '/';

  let isProcessingBundleRemoval = false;

  function wait(milliseconds) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });
  }

  async function getCart() {
    const response = await fetch(`${root}cart.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Could not retrieve the cart.');
    }

    return response.json();
  }

  async function clearCart() {
    const response = await fetch(`${root}cart/clear.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Could not rebuild the cart.');
    }

    return response.json();
  }

  async function addItems(items) {
    if (!items.length) return;

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({
        items
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description ||
        result.message ||
        'Could not rebuild the cart.'
      );
    }

    return result;
  }

  function removeBundleProperties(properties = {}) {
    const cleanProperties = {};

    Object.entries(properties).forEach(([name, value]) => {
      const isBundleProperty =
        name === '_bundle_id' ||
        name === '_bundle_parent_product' ||
        name === '_bundle_name' ||
        name === '_bundle_position' ||
        name === 'Bundle';

      if (!isBundleProperty && value !== null && value !== '') {
        cleanProperties[name] = value;
      }
    });

    return cleanProperties;
  }

  function buildCleanCartItems(
    cart,
    removedLineIndex,
    affectedBundleId
  ) {
    return cart.items
      .filter((item, index) => {
        return index !== removedLineIndex;
      })
      .map((item) => {
        const itemBundleId =
          item.properties?._bundle_id || '';

        const shouldConvertToNormal =
          affectedBundleId &&
          itemBundleId === affectedBundleId;

        const properties = shouldConvertToNormal
          ? removeBundleProperties(item.properties)
          : item.properties || {};

        const rebuiltItem = {
          id: item.variant_id,
          quantity: item.quantity
        };

        if (Object.keys(properties).length) {
          rebuiltItem.properties = properties;
        }

        if (item.selling_plan_allocation?.selling_plan?.id) {
          rebuiltItem.selling_plan =
            item.selling_plan_allocation.selling_plan.id;
        }

        return rebuiltItem;
      });
  }

  function getLineNumber(element) {
    const directLine =
      element.dataset.line ||
      element.dataset.index ||
      element.getAttribute('data-cart-item-index');

    if (directLine) {
      const parsedLine = Number(directLine);

      if (Number.isInteger(parsedLine) && parsedLine > 0) {
        return parsedLine;
      }
    }

    const lineContainer = element.closest(
      [
        '[data-line]',
        '[data-index]',
        '[data-cart-item-index]',
        '.cart-item',
        '.product-item'
      ].join(',')
    );

    if (lineContainer) {
      const containerLine =
        lineContainer.dataset.line ||
        lineContainer.dataset.index ||
        lineContainer.getAttribute(
          'data-cart-item-index'
        );

      if (containerLine) {
        const parsedLine = Number(containerLine);

        if (
          Number.isInteger(parsedLine) &&
          parsedLine > 0
        ) {
          return parsedLine;
        }
      }
    }

    const href =
      element.getAttribute('href') || '';

    if (href.includes('/cart/change')) {
      try {
        const url = new URL(
          href,
          window.location.origin
        );

        const line = Number(
          url.searchParams.get('line')
        );

        if (Number.isInteger(line) && line > 0) {
          return line;
        }
      } catch (error) {
        console.warn(
          '[Bundle Removal] Could not read line URL.',
          error
        );
      }
    }

    return null;
  }

  function isRemoveControl(element) {
    if (!element) return false;

    const removeSelector = [
      '[data-cart-remove]',
      '[data-remove-item]',
      '[data-remove]',
      '.cart-remove',
      '.cart-item__remove',
      '.product-remove',
      '.remove',
      'a[href*="/cart/change"][href*="quantity=0"]'
    ].join(',');

    return Boolean(element.closest(removeSelector));
  }

  function findRemoveControl(target) {
    return target.closest(
      [
        '[data-cart-remove]',
        '[data-remove-item]',
        '[data-remove]',
        '.cart-remove',
        '.cart-item__remove',
        '.product-remove',
        '.remove',
        'a[href*="/cart/change"][href*="quantity=0"]'
      ].join(',')
    );
  }

  function updateCartCount(cart) {
    const selectors = [
      '[data-cart-count]',
      '.cart-count-bubble',
      '.cart-count',
      '.header__cart-count',
      '.cartCount',
      '#cart-icon-bubble .count',
      '[data-cart-count-bubble]'
    ];

    document
      .querySelectorAll(selectors.join(','))
      .forEach((element) => {
        const countElement =
          element.querySelector('[aria-hidden="true"]') ||
          element.querySelector('span') ||
          element;

        countElement.textContent = cart.item_count;

        if (cart.item_count > 0) {
          element.hidden = false;
          element.classList.remove('hidden');
        }
      });
  }

  function dispatchCartRefresh(cart) {
    const detail = {
      cart,
      openCart: true,
      source: 'bundle-removal'
    };

    [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'halo:cart:updated'
    ].forEach((eventName) => {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail
        })
      );

      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail
        })
      );
    });
  }

  function findCartTrigger() {
    const selectors = [
      '[data-cart-sidebar]',
      '[data-open-cart]',
      '[data-cart-drawer-trigger]',
      '[data-cart-toggle]',
      '.header__icon--cart',
      '.header__icon-cart',
      '.cart-icon-bubble',
      '#cart-icon-bubble',
      '.header-cart',
      '.site-header__cart',
      'a[href="/cart"]'
    ];

    for (const selector of selectors) {
      const elements =
        document.querySelectorAll(selector);

      for (const element of elements) {
        const style =
          window.getComputedStyle(element);

        if (
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        ) {
          return element;
        }
      }
    }

    return null;
  }

  async function refreshEllaCart(cart) {
    updateCartCount(cart);
    dispatchCartRefresh(cart);

    await wait(150);

    if (
      typeof window.refreshCustomEllaCart ===
      'function'
    ) {
      try {
        await window.refreshCustomEllaCart({
          forceOpen: true,
          preserveDrawer: true
        });

        return;
      } catch (error) {
        console.warn(
          '[Bundle Removal] Custom refresh failed.',
          error
        );
      }
    }

    const cartTrigger = findCartTrigger();

    if (cartTrigger) {
      cartTrigger.dispatchEvent(
        new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        })
      );
    }
  }

  async function removeAndNormalizeBundle(lineNumber) {
    if (isProcessingBundleRemoval) return;

    isProcessingBundleRemoval = true;

    document.body.classList.add(
      'is-processing-bundle-removal'
    );

    try {
      const cart = await getCart();

      const removedLineIndex = lineNumber - 1;
      const removedItem = cart.items[removedLineIndex];

      if (!removedItem) {
        throw new Error(
          'The selected cart item could not be found.'
        );
      }

      const affectedBundleId =
        removedItem.properties?._bundle_id || '';

      /*
        * The item is not part of a custom bundle.
        * Let Ella handle normal products normally.
        */
      if (!affectedBundleId) {
        return false;
      }

      /*
        * Rebuild the cart:
        * - Exclude the removed item.
        * - Remove bundle properties from its siblings.
        * - Preserve unrelated products and their properties.
        */
      const cleanItems = buildCleanCartItems(
        cart,
        removedLineIndex,
        affectedBundleId
      );

      await clearCart();

      if (cleanItems.length) {
        await addItems(cleanItems);
      }

      await wait(150);

      const updatedCart = await getCart();

      await refreshEllaCart(updatedCart);

      return true;
    } finally {
      isProcessingBundleRemoval = false;

      document.body.classList.remove(
        'is-processing-bundle-removal'
      );
    }
  }

  document.addEventListener(
    'click',
    async (event) => {
      const removeControl =
        findRemoveControl(event.target);

      if (
        !removeControl ||
        !isRemoveControl(removeControl) ||
        isProcessingBundleRemoval
      ) {
        return;
      }

      const lineNumber =
        getLineNumber(removeControl);

      if (!lineNumber) return;

      /*
        * Check whether this line belongs to a bundle
        * before stopping Ella's normal removal.
        */
      let cart;

      try {
        cart = await getCart();
      } catch (error) {
        console.error(
          '[Bundle Removal]',
          error
        );

        return;
      }

      const selectedItem =
        cart.items[lineNumber - 1];

      const bundleId =
        selectedItem?.properties?._bundle_id;

      if (!bundleId) {
        /*
          * Normal product: Ella continues normally.
          */
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        await removeAndNormalizeBundle(lineNumber);
      } catch (error) {
        console.error(
          '[Bundle Removal]',
          error
        );

        /*
          * Fallback to the standard cart page when
          * rebuilding fails, preventing a broken cart.
          */
        window.location.href = `${root}cart`;
      }
    },
    true
  );
})();